import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Rating,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import {
    createWorkerFeedback,
    fetchWorkerFeedbackCommandStatus,
    fetchWorkerFeedback,
    publishWorkerFeedback,
    WorkerFeedbackEntry,
    WorkerFeedbackResponse,
    WorkerFeedbackPlacement,
    WorkerFeedbackVerdict,
} from '../../api/workerFeedbackApi';
import { extractErrorMessageSync } from '../../utils/errorHandler';
import { formatAmsterdamCalendarDate } from '../../utils/amsterdamCalendarDate';
import {
    COMMAND_STATUS_POLL_INITIAL_DELAY_MS,
    COMMAND_STATUS_POLL_MAX_TRANSIENT_FAILURES,
    commandStatusPollDelay,
    commandWasDefinitivelyRejected,
    getOrCreateFeedbackCommand,
    PendingFeedbackCommand,
} from './feedbackCommandKey';

const VERDICT_LABELS: Record<WorkerFeedbackVerdict, string> = {
    SUITABLE: 'Suitable for a new placement',
    RESERVE: 'Keep in reserve pool',
    NOT_SUITABLE: 'Not suitable for a new placement',
};

const END_REASON_LABELS = {
    COMPLETED: 'Completed',
    CLIENT_REQUEST: 'Client request',
    WORKER_REQUEST: 'Worker request',
    MUTUAL_AGREEMENT: 'Mutual agreement',
    OTHER: 'Other',
} as const;

function feedbackTitle(entry: WorkerFeedbackEntry): string {
    return entry.verdict ? VERDICT_LABELS[entry.verdict] : entry.title || 'Placement feedback';
}

function canPublish(entry: WorkerFeedbackEntry): boolean {
    return Boolean(entry.placementId && entry.verdict && entry.overallScore && entry.reviewText?.trim());
}

export function WorkerFeedbackPanel({
    workerId,
    canManageWorkers,
}: {
    workerId: string;
    canManageWorkers: boolean;
}) {
    const [data, setData] = useState<WorkerFeedbackResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [score, setScore] = useState<number | null>(null);
    const [endReason, setEndReason] = useState<keyof typeof END_REASON_LABELS | ''>('');
    const [reviewText, setReviewText] = useState('');
    const [selectedPlacementId, setSelectedPlacementId] = useState('');
    const [trackedCommand, setTrackedCommand] = useState<{
        kind: 'create' | 'publish';
        command: PendingFeedbackCommand;
    } | null>(null);
    const requestGeneration = useRef(0);
    const pendingCreate = useRef<PendingFeedbackCommand | null>(null);
    const pendingPublish = useRef<PendingFeedbackCommand | null>(null);
    const actionGeneration = useRef(0);
    const activeWorkerId = useRef(workerId);
    activeWorkerId.current = workerId;

    const missingPlacements = missingFeedbackPlacements(data);

    const load = useCallback(async () => {
        if (!canManageWorkers) return;
        if (activeWorkerId.current !== workerId) return;
        const generation = ++requestGeneration.current;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchWorkerFeedback(workerId);
            if (generation !== requestGeneration.current || activeWorkerId.current !== workerId) return;
            setData(response);
            const missing = missingFeedbackPlacements(response);
            setSelectedPlacementId((current) => missing.some((placement) => placement.id === current)
                ? current
                : missing[0]?.id ?? '');
        } catch (loadError) {
            if (generation !== requestGeneration.current || activeWorkerId.current !== workerId) return;
            setError(extractErrorMessageSync(loadError));
        } finally {
            if (generation === requestGeneration.current && activeWorkerId.current === workerId) setLoading(false);
        }
    }, [canManageWorkers, workerId]);

    useEffect(() => {
        requestGeneration.current += 1;
        setData(null);
        setScore(null);
        setEndReason('');
        setReviewText('');
        setSelectedPlacementId('');
        setTrackedCommand(null);
        actionGeneration.current += 1;
        setSaving(false);
        setNotice(null);
        void load();
        return () => {
            requestGeneration.current += 1;
        };
    }, [load]);

    useEffect(() => {
        if (!trackedCommand || !canManageWorkers) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let transientFailures = 0;
        const schedule = (delay: number) => {
            if (!cancelled) timer = setTimeout(() => void poll(), delay);
        };
        const poll = async () => {
            try {
                const result = await fetchWorkerFeedbackCommandStatus(
                    workerId,
                    trackedCommand.command.commandId,
                );
                if (cancelled || activeWorkerId.current !== workerId) return;
                if (result.state === 'SENT') {
                    const pending = trackedCommand.kind === 'create' ? pendingCreate : pendingPublish;
                    clearPendingCommand(pending, trackedCommand.command);
                    setTrackedCommand(null);
                    setNotice('Feedback delivered.');
                    setData(null);
                    if (trackedCommand.kind === 'create') {
                        setScore(null);
                        setEndReason('');
                        setReviewText('');
                    }
                    await load();
                    return;
                }
                if (result.state === 'FAILED') {
                    if (result.retryable === false) {
                        const pending = trackedCommand.kind === 'create' ? pendingCreate : pendingPublish;
                        clearPendingCommand(pending, trackedCommand.command);
                    }
                    setTrackedCommand(null);
                    setNotice(null);
                    setError(result.retryable === false
                        ? 'Feedback delivery was rejected.'
                        : 'Feedback delivery could not be confirmed. Retry to reconcile the same command.');
                    return;
                }
                transientFailures = 0;
            } catch (pollError) {
                if (cancelled) return;
                if (commandWasDefinitivelyRejected(pollError)) {
                    setTrackedCommand(null);
                    setNotice(null);
                    setError('Feedback command status is no longer accessible. Refresh your permissions or retry the same command.');
                    return;
                }
                transientFailures += 1;
                if (transientFailures >= COMMAND_STATUS_POLL_MAX_TRANSIENT_FAILURES) {
                    setTrackedCommand(null);
                    setNotice(null);
                    setError('Feedback command status is temporarily unavailable. Retry to reconcile the same command.');
                    return;
                }
            }
            schedule(commandStatusPollDelay(transientFailures));
        };
        schedule(COMMAND_STATUS_POLL_INITIAL_DELAY_MS);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [canManageWorkers, load, trackedCommand, workerId]);

    const submit = async () => {
        if (!canManageWorkers || !score || !endReason || !reviewText.trim() || !selectedPlacementId) return;
        const targetWorkerId = workerId;
        const payload = {
            placementId: selectedPlacementId,
            perspective: 'WORKER' as const,
            endReasonCode: endReason,
            reviewText: reviewText.trim() || undefined,
            overallScore: score,
            lifecycleStatus: 'PUBLISHED' as const,
        };
        const fingerprint = JSON.stringify(payload);
        const scope = `create:${targetWorkerId}`;
        const command = getOrCreateFeedbackCommand(pendingCreate.current, scope, fingerprint);
        pendingCreate.current = command;
        const generation = actionGeneration.current;
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            const result = await createWorkerFeedback(targetWorkerId, payload, command.commandId);
            if (activeWorkerId.current !== targetWorkerId || actionGeneration.current !== generation) return;
            const acknowledgedCommand = result.commandId === command.commandId
                ? command
                : { ...command, commandId: result.commandId };
            pendingCreate.current = acknowledgedCommand;
            if (result.state === 'FAILED') {
                if (result.retryable === false) clearPendingCommand(pendingCreate, acknowledgedCommand);
                if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) {
                    setError(result.retryable === false
                        ? 'Feedback was rejected and was not saved. Review the form and try again.'
                        : 'Feedback delivery could not be confirmed. Retry to reconcile the same command.');
                }
                return;
            }
            if (result.state === 'SENT') {
                clearPendingCommand(pendingCreate, acknowledgedCommand);
                setTrackedCommand(null);
                setData(null);
                setScore(null);
                setEndReason('');
                setReviewText('');
                await load();
            } else {
                setTrackedCommand({ kind: 'create', command: acknowledgedCommand });
                setNotice('Feedback command queued for delivery.');
            }
        } catch (saveError) {
            if (commandWasDefinitivelyRejected(saveError)) {
                clearPendingCommand(pendingCreate, command);
            }
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) {
                setError(extractErrorMessageSync(saveError));
            }
        } finally {
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) setSaving(false);
        }
    };

    const publish = async (entry: WorkerFeedbackEntry) => {
        if (!canManageWorkers) return;
        const targetWorkerId = workerId;
        const fingerprint = JSON.stringify({ lifecycleStatus: 'PUBLISHED' });
        const scope = `publish:${targetWorkerId}:${entry.id}`;
        const command = getOrCreateFeedbackCommand(pendingPublish.current, scope, fingerprint);
        pendingPublish.current = command;
        const generation = actionGeneration.current;
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            const result = await publishWorkerFeedback(targetWorkerId, entry.id, command.commandId);
            if (activeWorkerId.current !== targetWorkerId || actionGeneration.current !== generation) return;
            const acknowledgedCommand = result.commandId === command.commandId
                ? command
                : { ...command, commandId: result.commandId };
            pendingPublish.current = acknowledgedCommand;
            if (result.state === 'FAILED') {
                if (result.retryable === false) clearPendingCommand(pendingPublish, acknowledgedCommand);
                if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) {
                    setError(result.retryable === false
                        ? 'Feedback publication was rejected.'
                        : 'Feedback publication could not be confirmed. Retry to reconcile the same command.');
                }
                return;
            }
            if (result.state === 'SENT') {
                clearPendingCommand(pendingPublish, acknowledgedCommand);
                setTrackedCommand(null);
                setData(null);
                await load();
            } else {
                setTrackedCommand({ kind: 'publish', command: acknowledgedCommand });
                setNotice('Feedback publication queued for delivery.');
            }
        } catch (publishError) {
            if (commandWasDefinitivelyRejected(publishError)) {
                clearPendingCommand(pendingPublish, command);
            }
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) {
                setError(extractErrorMessageSync(publishError));
            }
        } finally {
            if (activeWorkerId.current === targetWorkerId && actionGeneration.current === generation) setSaving(false);
        }
    };

    if (!canManageWorkers) return null;

    if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}><CircularProgress /></Box>;

    if (!data) {
        return (
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h6" fontWeight={800}>Placement feedback</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Published ratings are used in candidate search and future offer checks.
                    </Typography>
                </Box>
                <Alert severity="error">{error || 'Feedback could not be loaded.'}</Alert>
                <Box><Button variant="outlined" onClick={() => void load()}>Retry</Button></Box>
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="h6" fontWeight={800}>Placement feedback</Typography>
                <Typography variant="body2" color="text.secondary">
                    Published ratings are used in candidate search and future offer checks.
                </Typography>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="info">{notice}</Alert>}
            {missingPlacements.length > 0 && (
                <Alert severity="warning">
                    Feedback is required for {missingPlacements.length === 1 ? 'an ended placement' : `${missingPlacements.length} ended placements`} before this candidate can be offered again.
                </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">Published average</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h4" fontWeight={800}>{data.summary.averageOverallScore ?? '-'}</Typography>
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`${data.summary.publishedCount ?? 0} published`} color="success" variant="outlined" />
                        {(data.summary.draftCount ?? 0) > 0 && <Chip label={`${data.summary.draftCount} draft`} color="warning" variant="outlined" />}
                    </Stack>
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>Record feedback</Typography>
                <Stack spacing={2}>
                    {missingPlacements.length === 0 && <Alert severity="info">There are no ended placements awaiting feedback.</Alert>}
                    {missingPlacements.length > 0 && (
                        <FormControl size="small" fullWidth>
                            <InputLabel id="worker-feedback-placement-label">Placement</InputLabel>
                            <Select labelId="worker-feedback-placement-label" id="worker-feedback-placement" value={selectedPlacementId} label="Placement" onChange={(event) => setSelectedPlacementId(event.target.value)}>
                                {missingPlacements.map((placement) => (
                                    <MenuItem key={placement.id} value={placement.id}>{placementLabel(placement)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Box>
                        <Typography component="legend" variant="body2" sx={{ mb: 0.5 }}>Rating (1-5)</Typography>
                        <Rating max={5} value={score} onChange={(_, value) => setScore(value)} emptyIcon={<StarOutlineIcon fontSize="inherit" />} />
                    </Box>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="worker-feedback-end-reason-label">End reason</InputLabel>
                        <Select labelId="worker-feedback-end-reason-label" id="worker-feedback-end-reason" value={endReason} label="End reason" onChange={(event) => setEndReason(event.target.value as keyof typeof END_REASON_LABELS)}>
                            {Object.entries(END_REASON_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField required label="Feedback" multiline minRows={3} value={reviewText} onChange={(event) => setReviewText(event.target.value)} />
                    <Box><Button variant="contained" onClick={() => void submit()} disabled={saving || Boolean(trackedCommand) || !selectedPlacementId || !score || !endReason || !reviewText.trim()}>Publish feedback</Button></Box>
                </Stack>
            </Paper>

            <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={800}>History</Typography>
                {data.items.length === 0 ? <Alert severity="info">No feedback has been recorded.</Alert> : data.items.map((entry) => (
                    <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="subtitle2" fontWeight={700}>{feedbackTitle(entry)}</Typography>
                                    <Chip size="small" label={entry.lifecycleStatus.toLowerCase()} color={entry.lifecycleStatus === 'PUBLISHED' ? 'success' : 'default'} />
                                    {entry.overallScore != null && <Chip size="small" label={`${entry.overallScore}/${entry.ratingScale ?? 10}`} variant="outlined" />}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{new Date(entry.createdAt).toLocaleString()}</Typography>
                                {entry.reviewText && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{entry.reviewText}</Typography>}
                            </Box>
                            {entry.lifecycleStatus === 'DRAFT' && (
                                <Tooltip title={canPublish(entry) ? '' : 'Complete the placement, verdict, rating, and feedback first'}>
                                    <span>
                                        <Button size="small" startIcon={<PublishOutlinedIcon />} onClick={() => void publish(entry)} disabled={saving || Boolean(trackedCommand) || !canPublish(entry)}>Publish</Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        </Stack>
    );
}

function clearPendingCommand(
    pending: { current: PendingFeedbackCommand | null },
    completed: PendingFeedbackCommand,
): void {
    const current = pending.current;
    if (
        current?.scope === completed.scope &&
        current.commandId === completed.commandId &&
        current.fingerprint === completed.fingerprint
    ) {
        pending.current = null;
    }
}

function missingFeedbackPlacements(data: WorkerFeedbackResponse | null): WorkerFeedbackPlacement[] {
    if (!data) return [];
    if (Array.isArray(data.endedPlacementsMissingFeedback)) return data.endedPlacementsMissingFeedback;
    return data.latestEndedPlacement && data.latestEndedPlacementFeedbackMissing
        ? [data.latestEndedPlacement]
        : [];
}

function placementLabel(placement: WorkerFeedbackPlacement): string {
    const reference = placement.easyflexPlacementNumber
        ? `#${placement.easyflexPlacementNumber}`
        : placement.title || 'Placement';
    return placement.endDate ? `${reference} ended ${formatAmsterdamCalendarDate(placement.endDate)}` : reference;
}
